import { useCallback, useMemo, useState } from "react";

/**
 * F12 — Seleção múltipla reutilizável para tabelas com checkbox por linha
 * + select-all no header. Item genérico T precisa ter `id: number`.
 *
 * Exemplo de uso:
 *
 *   const rows = listQuery.data?.rows ?? [];
 *   const selection = useBulkSelection(rows);
 *   ...
 *   <input type="checkbox"
 *     checked={selection.isAllSelected}
 *     onChange={(e) => e.target.checked ? selection.selectAll() : selection.clear()} />
 *   ...
 *   <input type="checkbox"
 *     checked={selection.isSelected(row.id)}
 *     onChange={() => selection.toggle(row.id)} />
 *   ...
 *   if (selection.count > 0) <BulkActionBar count={selection.count} onClear={selection.clear}>...
 *
 * Quando a página muda (rows muda de referência), `selection.selected` mantém
 * os ids — caller deve chamar `clear()` no onChange do filtro/pagination
 * quando quiser invalidar a seleção. Esse mantém o controle no caller.
 */
export interface BulkSelection<T extends { id: number }> {
  /** Set imutável dos ids selecionados. */
  selected: ReadonlySet<number>;
  /** Contagem. */
  count: number;
  /** Itens da página atual que estão selecionados (preserva ordem da pagina). */
  selectedItems: ReadonlyArray<T>;
  /** Todos os itens da página atual estão selecionados. */
  isAllSelected: boolean;
  isSelected: (id: number) => boolean;
  toggle: (id: number) => void;
  /** Seleciona todos os items da página atual (mescla com os já selecionados). */
  selectAll: () => void;
  /** Limpa toda a seleção. */
  clear: () => void;
  /** Remove um id específico (útil para "X" inline ou bulk action que processa item por item). */
  remove: (id: number) => void;
}

export function useBulkSelection<T extends { id: number }>(
  items: ReadonlyArray<T>,
): BulkSelection<T> {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  const isSelected = useCallback((id: number) => selected.has(id), [selected]);

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const item of items) next.add(item.id);
      return next;
    });
  }, [items]);

  const clear = useCallback(() => {
    setSelected(new Set());
  }, []);

  const remove = useCallback((id: number) => {
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectedItems = useMemo(
    () => items.filter((item) => selected.has(item.id)),
    [items, selected],
  );

  const isAllSelected = useMemo(
    () => items.length > 0 && items.every((item) => selected.has(item.id)),
    [items, selected],
  );

  return {
    selected,
    count: selected.size,
    selectedItems,
    isAllSelected,
    isSelected,
    toggle,
    selectAll,
    clear,
    remove,
  };
}
